import { useQuery } from "@tanstack/react-query";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { PremiumCallout } from "components/ui/premium-callout";
import { primaryClasses } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { useProductName } from "store/selectors/branding";
import {
    useCurrentRegion,
    useFeature,
    useIsMultiRegionUser,
    useRegionId,
} from "store/selectors/hooks";
import { OtherQuickBooksConnections } from "./other-quick-books-connections";
import { QuickBooksConnection } from "./quick-books-connection";
import { useHipaaAcknowledgement } from "./use-hipaa-acknowledgement";

export const ManageIntegrationsQuickBooks: React.FC = () => {
    const isFeatureEnabled = useFeature("quickbooks");
    const productName = useProductName();

    if (isFeatureEnabled) {
        return <ManageIntegrationsQuickBooksActual />;
    }

    return (
        <ContentFull>
            <ContentHeader>QuickBooks</ContentHeader>
            <PremiumCallout>
                <InlineBanner type="info">
                    QuickBooks is only available on request to {productName} Premium
                    customers. <br />
                    Please{" "}
                    <SupportEmailLink
                        label="contact support"
                        subject="QuickBooks Online"
                    />{" "}
                    to request an upgrade.
                </InlineBanner>
            </PremiumCallout>
        </ContentFull>
    );
};

export const ManageIntegrationsQuickBooksActual: React.FC<unknown> = () => {
    const regionId = useRegionId();
    const currentRegion = useCurrentRegion();
    const isMultiRegion = useIsMultiRegionUser();
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.integrations.getQuickBooksCredential.queryOptions({
            regionId,
        }),
        enabled: !!regionId,
    });
    const allCredentialsQuery = useQuery({
        ...trpcClient.integrations.getQuickBooksCredentials.queryOptions({}),
    });
    const { confirmHipaaRisk } = useHipaaAcknowledgement();
    const productName = useProductName();

    const credential = query.data;

    const handleConnect = useCallback(async () => {
        const confirmed = await confirmHipaaRisk("QuickBooks");
        if (confirmed) {
            window.location.href = `/api/auth/quickbooks?regionId=${regionId}`;
        }
    }, [confirmHipaaRisk, regionId]);

    const handleRefetch = useCallback(() => {
        query.refetch();
        allCredentialsQuery.refetch();
    }, [query, allCredentialsQuery]);

    if (query.isLoading) {
        return <Spinner />;
    }

    if (!credential) {
        return (
            <div className="space-y-4 lg:space-y-8">
                <ContentFull>
                    <ContentHeader>QuickBooks</ContentHeader>
                    <div className="space-y-4">
                        <div>
                            In order to set up the QuickBooks integration, please click{" "}
                            <strong>Connect to QuickBooks</strong> below. You will be
                            taken to your QuickBooks online account and will be asked to
                            give permission to {productName} in order to access your data.
                            Once that is complete, you will be returned to {productName}.
                        </div>
                        <div>
                            <button onClick={handleConnect} className={primaryClasses}>
                                Connect to QuickBooks
                            </button>
                        </div>
                    </div>
                </ContentFull>
                <OtherQuickBooksConnections
                    currentCredentialId={null}
                    onChanged={handleRefetch}
                />
            </div>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <QuickBooksConnection
                credential={credential}
                onDeleted={handleRefetch}
                onSaved={handleRefetch}
                onConnect={handleConnect}
                regionId={regionId}
                activeForRegionName={isMultiRegion ? currentRegion?.name : undefined}
            />
            <OtherQuickBooksConnections
                currentCredentialId={credential.id}
                onChanged={handleRefetch}
            />
        </div>
    );
};
